import { customElement, DOM, FASTElement, observable } from '@microsoft/fast-element';
import { AutoExecuteApprovedCounterpartiesModalTemplate as template } from './auto-execute-approved-counterparties-modal.template';
import { AutoExecuteApprovedCounterpartiesModalStyles as styles } from './auto-execute-approved-counterparties-modal.styles';
import { ICompanyService } from '../../../services/company.service';
import { ICommonService } from '../../../services';
import { GOVERNING_AGREEMENT, ICompanyApproval } from '../../../types';
import { IAutoExecuteApprovedCounterpartiesService } from '../../../services/auto-execute-approved-counterparties.service';

const name = 'auto-execute-approved-counterparties-modal';

@customElement({
  name,
  template,
  styles,
})
export class AutoExecuteApprovedCounterpartiesModal extends FASTElement {
  @ICompanyService companyService: ICompanyService;
  @ICommonService commonService: ICommonService;
  @IAutoExecuteApprovedCounterpartiesService
  autoExecuteApprovedCounterpartiesService: IAutoExecuteApprovedCounterpartiesService;

  @observable public companyApprovals: Array<ICompanyApproval> = [];
  @observable public companyApprovalsInitial: Array<ICompanyApproval> = [];

  @observable public isFormValidChanged: boolean = true;
  @observable public newValueAdded: boolean = false;
  @observable public isCompanyApprovalsLoaded: boolean = false;
  @observable public isUpdate: boolean = false;
  @observable isBusinessHours: boolean;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadBusinessHourFlag();

    DOM.queueUpdate(async () => {
      // 1. Get all companies that have marked auto-execution flag
      await this.companyService
        .getAllCompaniesAdmin(
            `CASCADE_COMPANY_ID != '${this.companyService.getCurrentCompany().CASCADE_COMPANY_ID}' && CASCADE_PARENT_COMPANY_ID != '${this.companyService.getCurrentCompany().CASCADE_COMPANY_ID}'`
        )
        .then((response) => {
          if (response && response.ROW) {
            this.companyApprovals = response.ROW.map((company) => ({
              IS_CHECKED: false,
              CASCADE_COMPANY_ID: this.companyService.getCurrentCompany().CASCADE_COMPANY_ID,
              CASCADE_COMPANY_NAME: company.NAME,
              COUNTERPARTY_COMPANY_ID: company.CASCADE_COMPANY_ID,
              GOVERNING_AGREEMENT: GOVERNING_AGREEMENT.EXISTING_ISDA_MASTER_AGREEMENT,
              EFFECTIVE_DATE: null,
            }));
          }
        });

      // 2. Get the approved counterparties
    await this.autoExecuteApprovedCounterpartiesService
      .getAutoExecuteApprovedCounterpartiesByCascadeCompany()
      .then((response) => {
        if (response && response.ROW) {
          this.isUpdate = response.ROW.length > 0;
          response.ROW.forEach((autoExecuteApprovedCounterparty) => {
            // Skip records where CASCADE_PARENT_COMPANY_ID matches the current company ID
            if (
              autoExecuteApprovedCounterparty.CASCADE_PARENT_COMPANY_ID !==
              this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
            ) {
              this.companyApprovals = this.companyApprovals.map((company) => {
                if (
                  company.COUNTERPARTY_COMPANY_ID ===
                  autoExecuteApprovedCounterparty.COUNTERPARTY_COMPANY_ID
                ) {
                  company.IS_CHECKED = true;
                  company.GOVERNING_AGREEMENT = autoExecuteApprovedCounterparty.GOVERNING_AGREEMENT;
                  company.EFFECTIVE_DATE = autoExecuteApprovedCounterparty.EFFECTIVE_DATE;
                }
                return company;
              });
            }
          });
        }
      });


      this.isCompanyApprovalsLoaded = true;
      this.companyApprovalsInitial = this.companyApprovals.filter(it => it.IS_CHECKED)
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  close() {
    this.companyApprovals = [];
    this.isFormValidChanged = true;
    this.isCompanyApprovalsLoaded = false;
    this.isUpdate = false;
    this.$emit('close');
  }

  async loadBusinessHourFlag() {
    await this.commonService.isItIsBuyingHours().then((response) => {
      if (response.REPLY) {
       this.isBusinessHours = response.REPLY[0].IS_IT_IS_BUYING_HOURS
      }
    });
  }


  approvalAdded(approval: ICompanyApproval) {
    this.companyApprovals = this.companyApprovals.map((companyApproval) =>
      companyApproval.COUNTERPARTY_COMPANY_ID === approval.COUNTERPARTY_COMPANY_ID
        ? approval
        : companyApproval
    );

    this.validateForm();
  }

  validateForm() {
    // No companies to approve (disable button by default)
    if (!this.companyApprovals || this.companyApprovals.length === 0) {
      this.isFormValidChanged = true;
      return;
    }

    // No company selected and is not update (because if it is,
    // it's possible to unselect all companies)
    if (
      this.companyApprovals.filter((companyApproval) => companyApproval.IS_CHECKED).length == 0 &&
      !this.isUpdate
    ) {
      this.isFormValidChanged = true;
      return;
    }

    // Some company is selected, but one governing agreement is
    // EXISTING_ISDA_MASTER_AGREEMENT and its effective date is missing
    if (this.companyApprovals.filter(this.isCheckedAndIsEIMAWithDateMissing).length > 0) {
      this.isFormValidChanged = true;
      return;
    }
    // If some companies are added or it is updated check
    // not allow it to happen in not business hours
    this.newValueAdded = false;
    this.companyApprovals.filter(it => it.IS_CHECKED).some(fromSelected => {
      const newAdded = this.companyApprovalsInitial.find(
        fromInitial => fromInitial.COUNTERPARTY_COMPANY_ID === fromSelected.COUNTERPARTY_COMPANY_ID
      );
      if (!newAdded) {
        this.newValueAdded = true;
      }
    })
    if (this.newValueAdded) {
       this.isFormValidChanged = !this.isBusinessHours;
       return;
    }

    this.isFormValidChanged = false;
  }

  isCheckedAndIsEIMAWithDateMissing(companyApproval: ICompanyApproval) {
    return (
      companyApproval.IS_CHECKED &&
      companyApproval.GOVERNING_AGREEMENT == GOVERNING_AGREEMENT.EXISTING_ISDA_MASTER_AGREEMENT &&
      !companyApproval.EFFECTIVE_DATE
    );
  }

  savePreferences() {
    this.autoExecuteApprovedCounterpartiesService
      .upsertAutoExecuteApprovedCounterparties(this.companyApprovals)
      .then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            return;
          } else {
            return null;
          }
        },
        (error) => {
          return null;
        }
      );
    this.close();
  }
}
